import React from 'react';
import type { DashboardAnnualDaySlot } from '../../utils/dashboardAnnualDaySlots';
import type { DailyNoteCategoryRow, DailyWeekNoteDraft } from '../../types/dailyRecordNote';
import type { TodoNoteItemView } from '../../types/todoNote';
import DashboardWeeklyPlanner, { type DashboardWeeklyTodoRow } from './DashboardWeeklyPlanner';
import DashboardWeeklyNotesPlanner from './DashboardWeeklyNotesPlanner';

type Props = {
  mondayIso: string;
  interactive: boolean;
  weeklyNotesMode: boolean;
  holidayIsoSet: ReadonlySet<string>;
  holidayNoteByIso: Readonly<Record<string, string>>;
  slotsByDateIso: Readonly<Record<string, DashboardAnnualDaySlot[]>>;
  todosByDateIso: Readonly<Record<string, DashboardWeeklyTodoRow[]>>;
  behaviorRecordKeySet: ReadonlySet<string>;
  periodTimeByKey?: Readonly<Record<string, { start: string; end: string }>>;
  daysVertical: boolean;
  weekNotes: DailyWeekNoteDraft[];
  savedWeekNotes: DailyWeekNoteDraft[];
  dailyNoteCategories: DailyNoteCategoryRow[];
  contentsHidden: boolean;
  onAddTodo?: (dateIso: string) => void;
  onOpenTodo?: (item: TodoNoteItemView) => void;
  onOpenPresentation?: (slot: DashboardAnnualDaySlot) => void;
  onOpenProgram?: (slot: DashboardAnnualDaySlot) => void;
  onOpenLessonRecord?: (slot: DashboardAnnualDaySlot) => void;
  onOpenBehaviorRecord?: (slot: DashboardAnnualDaySlot) => void;
  onChangeWeekNotes?: (notes: DailyWeekNoteDraft[]) => void;
  onUnlockRequest?: () => void;
};

const DashboardWeeklyWeekPane: React.FC<Props> = ({
  mondayIso,
  interactive,
  weeklyNotesMode,
  holidayIsoSet,
  holidayNoteByIso,
  slotsByDateIso,
  todosByDateIso,
  behaviorRecordKeySet,
  periodTimeByKey,
  daysVertical,
  weekNotes,
  savedWeekNotes,
  dailyNoteCategories,
  contentsHidden,
  onAddTodo,
  onOpenTodo,
  onOpenPresentation,
  onOpenProgram,
  onOpenLessonRecord,
  onOpenBehaviorRecord,
  onChangeWeekNotes,
  onUnlockRequest,
}) => {
  return (
    <div
      className="cp-dash-weekly-week-pane"
      aria-hidden={!interactive}
      inert={!interactive}
    >
      <div className="cp-dash-weekly-stack">
        <div className={'cp-dash-weekly-stack-track' + (weeklyNotesMode ? ' is-notes' : '')}>
          <div className="cp-dash-weekly-stack-pane" aria-hidden={weeklyNotesMode}>
            <DashboardWeeklyPlanner
              mondayIso={mondayIso}
              holidayIsoSet={holidayIsoSet}
              holidayNoteByIso={holidayNoteByIso}
              slotsByDateIso={slotsByDateIso}
              todosByDateIso={todosByDateIso}
              behaviorRecordKeySet={behaviorRecordKeySet}
              periodTimeByKey={periodTimeByKey}
              daysVertical={daysVertical}
              onAddTodo={interactive ? onAddTodo : undefined}
              onOpenTodo={interactive ? onOpenTodo : undefined}
              onOpenPresentation={interactive ? onOpenPresentation : undefined}
              onOpenProgram={interactive ? onOpenProgram : undefined}
              onOpenLessonRecord={interactive ? onOpenLessonRecord : undefined}
              onOpenBehaviorRecord={interactive ? onOpenBehaviorRecord : undefined}
            />
          </div>
          <div className="cp-dash-weekly-stack-pane" aria-hidden={!weeklyNotesMode}>
            <DashboardWeeklyNotesPlanner
              mondayIso={mondayIso}
              holidayIsoSet={holidayIsoSet}
              holidayNoteByIso={holidayNoteByIso}
              daysVertical={daysVertical}
              notes={weekNotes}
              savedNotes={savedWeekNotes}
              categories={dailyNoteCategories}
              onChangeNotes={interactive && onChangeWeekNotes ? onChangeWeekNotes : () => undefined}
              contentsHidden={contentsHidden}
              onUnlockRequest={interactive ? onUnlockRequest : undefined}
              interactive={interactive}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardWeeklyWeekPane;
